/**
 * Labstep - BlockNote-style Table Handles
 *
 * Implements individual row and column handles that appear on hover
 * with menu functionality and drag-to-rearrange capability
 */

import { HandleMenu } from 'labstep-web/prosemirror/components/TableHandles/HandleMenu';
import { TableDeleteHandle } from 'labstep-web/prosemirror/components/TableHandles/TableDeleteHandle';
import { TableEdgeHandle } from 'labstep-web/prosemirror/components/TableHandles/TableEdgeHandle';
import { TableRowColumnHandle } from 'labstep-web/prosemirror/components/TableHandles/TableRowColumnHandle';
import type { TableHandlesProps } from 'labstep-web/prosemirror/components/TableHandles/types';
import {
  calculateEdgeHandlePositions,
  findTargetTable,
  getPositionsForTable,
  type HandlePosition,
  type MousePosition,
  selectCellInTarget,
} from 'labstep-web/prosemirror/components/TableHandles/utils';
import {
  addColumnAfter,
  addColumnBefore,
  addRowAfter,
  addRowBefore,
  deleteColumn,
  deleteRow,
} from 'prosemirror-tables';
import React, {
  type FC,
  useCallback,
  useEffect,
  useState,
} from 'react';

export const TableHandles: FC<TableHandlesProps> = ({
  view,
  tableNode,
  tablePos,
  hoveredCell,
}) => {
  const [menuState, setMenuState] = useState<{
    isOpen: boolean;
    position: { top: number; left: number };
    type: 'row' | 'column';
    index: number;
  } | null>(null);

  const [lastMousePosition, setLastMousePosition] =
    useState<MousePosition | null>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const [frozenPositions, setFrozenPositions] = useState<{
    rows: HandlePosition[];
    cols: HandlePosition[];
  } | null>(null);

  // Track mouse position for table detection
  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      setLastMousePosition({ x: event.clientX, y: event.clientY });
    };

    let scrollTimer: NodeJS.Timeout;
    const handleScroll = (event: Event) => {
      // Hide handles during scroll
      setIsScrolling(true);

      // Clear existing timer
      clearTimeout(scrollTimer);

      // Show handles again after scroll ends
      scrollTimer = setTimeout(() => {
        setIsScrolling(false);
        // Force a re-render when scrolling ends to update handle positions
        setLastMousePosition((prev) => (prev ? { ...prev } : prev));
      }, 150);
    };

    // Add mouse move listener
    document.addEventListener('mousemove', handleMouseMove, {
      passive: true,
    });

    // Start with just window scroll to test
    window.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    // Also add to document itself for better coverage
    document.addEventListener('scroll', handleScroll, {
      passive: true,
      capture: true,
    });

    // Also try document.body and documentElement
    document.body.addEventListener('scroll', handleScroll, {
      passive: true,
    });
    document.documentElement.addEventListener(
      'scroll',
      handleScroll,
      { passive: true },
    );

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll, true); // Remove capture listener
      document.body.removeEventListener('scroll', handleScroll);
      document.documentElement.removeEventListener(
        'scroll',
        handleScroll,
      );
      clearTimeout(scrollTimer);
    };
  }, [view]);

  // Calculate table cell positions for the specific table being hovered
  const getTableCellPositions = useCallback(() => {
    if (!hoveredCell) {
      return { rows: [], cols: [], targetTable: null };
    }

    // Use frozen positions when menu is open to prevent handles from moving
    if (menuState?.isOpen && frozenPositions) {
      return {
        rows: frozenPositions.rows,
        cols: frozenPositions.cols,
        targetTable: null,
      };
    }

    const editorElement = view.dom;
    const targetTable = findTargetTable(
      editorElement,
      lastMousePosition,
      hoveredCell,
    );

    if (!targetTable) {
      return { rows: [], cols: [], targetTable: null };
    }

    const { rows, cols } = getPositionsForTable(targetTable);
    return { rows, cols, targetTable };
  }, [
    view,
    hoveredCell,
    lastMousePosition,
    menuState,
    frozenPositions,
  ]);

  const { rows, cols } = getTableCellPositions();

  // Calculate edge handle positions (bottom and right edges)
  const { bottomEdge, rightEdge } = hoveredCell
    ? calculateEdgeHandlePositions(hoveredCell, rows, cols)
    : { bottomEdge: null, rightEdge: null };

  const handleMenuOpen = useCallback(
    (
      position: { top: number; left: number },
      type: 'row' | 'column',
      index: number,
    ) => {
      // Freeze current positions before opening menu
      const currentPositions = getTableCellPositions();
      setFrozenPositions({
        rows: currentPositions.rows,
        cols: currentPositions.cols,
      });

      setMenuState({ isOpen: true, position, type, index });
    },
    [getTableCellPositions],
  );

  const handleMenuClose = useCallback(() => {
    setMenuState(null);
    setFrozenPositions(null); // Clear frozen positions when menu closes
  }, []);

  // Menu action handlers
  const handleAddBefore = useCallback(() => {
    if (!menuState || !view || !hoveredCell || !tableNode) {
      return;
    }

    // Determine target cell coordinates - use the clicked handle's row/column
    const targetRow =
      menuState.type === 'row' ? menuState.index : hoveredCell.row;
    const targetCol =
      menuState.type === 'column' ? menuState.index : hoveredCell.col;

    // Select a cell in the target row/column first
    if (
      selectCellInTarget(
        view,
        tableNode,
        tablePos,
        targetRow,
        targetCol,
      )
    ) {
      // Small delay to ensure selection is applied
      setTimeout(() => {
        const { state, dispatch } = view;
        const command =
          menuState.type === 'row' ? addRowBefore : addColumnBefore;

        if (command(state)) {
          command(state, dispatch);
        }
      }, 10);
    }

    handleMenuClose();
  }, [
    menuState,
    view,
    hoveredCell,
    tableNode,
    tablePos,
    handleMenuClose,
  ]);

  const handleAddAfter = useCallback(() => {
    if (!menuState || !view || !hoveredCell || !tableNode) {
      return;
    }

    // Determine target cell coordinates - use the clicked handle's row/column
    const targetRow =
      menuState.type === 'row' ? menuState.index : hoveredCell.row;
    const targetCol =
      menuState.type === 'column' ? menuState.index : hoveredCell.col;

    // Select a cell in the target row/column first
    if (
      selectCellInTarget(
        view,
        tableNode,
        tablePos,
        targetRow,
        targetCol,
      )
    ) {
      // Small delay to ensure selection is applied
      setTimeout(() => {
        const { state, dispatch } = view;
        const command =
          menuState.type === 'row' ? addRowAfter : addColumnAfter;

        if (command(state)) {
          command(state, dispatch);
        }
      }, 10);
    }

    handleMenuClose();
  }, [
    menuState,
    view,
    hoveredCell,
    tableNode,
    tablePos,
    handleMenuClose,
  ]);

  const handleDelete = useCallback(() => {
    if (!menuState || !view || !hoveredCell || !tableNode) {
      return;
    }

    // Determine target cell coordinates - use the clicked handle's row/column
    const targetRow =
      menuState.type === 'row' ? menuState.index : hoveredCell.row;
    const targetCol =
      menuState.type === 'column' ? menuState.index : hoveredCell.col;

    // Select a cell in the target row/column first
    if (
      selectCellInTarget(
        view,
        tableNode,
        tablePos,
        targetRow,
        targetCol,
      )
    ) {
      // Small delay to ensure selection is applied
      setTimeout(() => {
        const { state, dispatch } = view;
        const command =
          menuState.type === 'row' ? deleteRow : deleteColumn;

        if (command(state)) {
          command(state, dispatch);
        }
      }, 10);
    }

    handleMenuClose();
  }, [
    menuState,
    view,
    hoveredCell,
    tableNode,
    tablePos,
    handleMenuClose,
  ]);

  const handleAddRowAtEnd = useCallback(() => {
    if (!view || !hoveredCell || !tableNode) {
      return;
    }

    // Select the last cell in the table to ensure we're adding at the end
    const lastRowIndex = rows.length - 1;
    if (
      selectCellInTarget(view, tableNode, tablePos, lastRowIndex, 0)
    ) {
      setTimeout(() => {
        const { state, dispatch } = view;
        if (addRowAfter(state)) {
          addRowAfter(state, dispatch);
        }
      }, 10);
    }
  }, [view, hoveredCell, rows.length, tableNode, tablePos]);

  const handleAddColumnAtEnd = useCallback(() => {
    if (!view || !hoveredCell || !tableNode) {
      return;
    }

    // Select the last cell in the table to ensure we're adding at the end
    const lastColIndex = cols.length - 1;
    if (
      selectCellInTarget(view, tableNode, tablePos, 0, lastColIndex)
    ) {
      setTimeout(() => {
        const { state, dispatch } = view;
        if (addColumnAfter(state)) {
          addColumnAfter(state, dispatch);
        }
      }, 10);
    }
  }, [view, hoveredCell, cols.length, tableNode, tablePos]);

  // Delete whole table
  const handleDeleteTable = useCallback(() => {
    if (!view || !tableNode) {
      return;
    }
    const { state, dispatch } = view;
    // Remove the table node from the document
    dispatch(
      state.tr
        .delete(tablePos, tablePos + tableNode.nodeSize)
        .scrollIntoView(),
    );
  }, [view, tableNode, tablePos]);

  // Compute top-left delete handle position (slightly outside table bounds)
  const topLeftDeleteHandle =
    rows.length && cols.length
      ? {
          top: rows[0].top - 25,
          left: cols[0].left - 25,
        }
      : null;

  // Only show handles if we have a hovered cell and not scrolling
  if (!hoveredCell || isScrolling) {
    return (
      <>
        {menuState && !isScrolling && (
          <HandleMenu
            isOpen={menuState.isOpen}
            position={menuState.position}
            onClose={handleMenuClose}
            onAddBefore={handleAddBefore}
            onAddAfter={handleAddAfter}
            onDelete={handleDelete}
            type={menuState.type}
          />
        )}
      </>
    );
  }

  return (
    <>
      <TableDeleteHandle
        position={topLeftDeleteHandle}
        onDelete={handleDeleteTable}
        isVisible={Boolean(hoveredCell)}
      />

      {/* Row handle for the hovered row */}
      {rows[hoveredCell.row] && (
        <TableRowColumnHandle
          position={{
            top: rows[hoveredCell.row].top,
            left: rows[hoveredCell.row].left,
          }}
          size={{
            width: rows[hoveredCell.row].width,
            height: rows[hoveredCell.row].height,
          }}
          type="row"
          onMenuOpen={(pos) =>
            handleMenuOpen(pos, 'row', hoveredCell.row)
          }
        />
      )}

      {/* Column handle for the hovered column */}
      {cols[hoveredCell.col] && (
        <TableRowColumnHandle
          position={{
            top: cols[hoveredCell.col].top,
            left: cols[hoveredCell.col].left,
          }}
          size={{
            width: cols[hoveredCell.col].width,
            height: cols[hoveredCell.col].height,
          }}
          type="column"
          onMenuOpen={(pos) =>
            handleMenuOpen(pos, 'column', hoveredCell.col)
          }
        />
      )}

      {/* Bottom edge handle - appears when hovering over last row */}
      {bottomEdge && (
        <TableEdgeHandle
          position={{
            top: bottomEdge.top,
            left: bottomEdge.left,
          }}
          size={{
            width: bottomEdge.width,
            height: bottomEdge.height,
          }}
          type="bottom"
          onClick={handleAddRowAtEnd}
        />
      )}

      {/* Right edge handle - appears when hovering over last column */}
      {rightEdge && (
        <TableEdgeHandle
          position={{
            top: rightEdge.top,
            left: rightEdge.left,
          }}
          size={{
            width: rightEdge.width,
            height: rightEdge.height,
          }}
          type="right"
          onClick={handleAddColumnAtEnd}
        />
      )}
      {/* Context menu */}
      {menuState && (
        <HandleMenu
          isOpen={menuState.isOpen}
          position={menuState.position}
          onClose={handleMenuClose}
          onAddBefore={handleAddBefore}
          onAddAfter={handleAddAfter}
          onDelete={handleDelete}
          type={menuState.type}
        />
      )}
    </>
  );
};
